'use client'

import { useState, useRef, useEffect } from 'react'

interface LazyBackgroundVideoProps {
  src: string
  className?: string
  children?: React.ReactNode
  fallbackColor?: string
}

export default function LazyBackgroundVideo({
  src,
  className = '',
  children,
  fallbackColor = 'bg-black',
}: LazyBackgroundVideoProps) {
  const [isLoaded, setIsLoaded] = useState(false)
  const [isInView, setIsInView] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )

    if (containerRef.current) {
      observer.observe(containerRef.current)
    }

    return () => observer.disconnect()
  }, [])

  const handleVideoLoad = () => {
    setIsLoaded(true)
  }

  return (
    <div ref={containerRef} className={`relative overflow-hidden ${className}`}>
      {/* Fallback background */}
      <div className={`absolute inset-0 ${fallbackColor}`} />

      {/* Video - only load when in view */}
      {isInView && (
        <video
          ref={videoRef}
          src={src}
          className={`absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-500 ${
            isLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          autoPlay
          loop
          muted
          playsInline
          preload="metadata"
          onLoadedData={handleVideoLoad}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
          }}
        />
      )}

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Content */}
      {children}
    </div>
  )
}
